<template>
  <div class="slider">
    <Swiper
      class="slider__swiper"
      @mousemove.native="movehandler"
      @swipe="swipehandler"
      @swipeend="swipehandler"
      @swipeabort="swipehandler"
    >
      <div
        ref="scroller"
        class="slider__wrapper"
      >
        <ul
          v-if="items"
          ref="scrollerContent"
          class="slider__items"
          :style="`transform: translate3d(${translateX + swipeX}px, 0, 0)`"
        >
          <li
            v-for="(item, index) in items"
            :key="index"
            class="slider__item"
          >
            <slot
              name="item"
              :item="item"
            >
              <p>item {{ item.id || 'id inconnu' }}</p>
            </slot>
          </li>
        </ul>
      </div>
    </Swiper>

    <div class="slider__navigation">
      <div
        class="slider__button-wrapper"
        :style="{ opacity: index > 0 ? 1 : 0}"
        @click="previous"
      >
        <slot name="previous">
          <button>prev</button>
        </slot>
      </div>

      <div
        class="slider__button-wrapper"
        :style="{ opacity: index < items.length - 1 ? 1 : 0}"
        @click="next"
      >
        <slot name="next">
          <button>next</button>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from '@/components/Swiper.vue';
import { isTouchDevice } from '@/components/utils/touchDevice';

const SWIPE_ENABLED = false;
const SPEED_MIN = 0.00001;
const SPEED_MAX = 0.05;

function clamp(min, value, max) {
  return Math.min(max, Math.max(value, min));
}

export default {
  components: { Swiper },

  props: {
    items: { type: Array, default: null },
  },

  data() {
    return {
      swipeX: 0,
      translateX: 0,
      speed: 0,
      distance: 0,
      index: 0,
      state: {
        touchDevice: false,
      },
    };
  },

  mounted() {
    this.rafID = null;

    this.smoothMove = true;
    this.ratio = 0;
    this.isMoving = false;

    window.addEventListener('resize', this.resizeHandler);
    this.resizeHandler();
  },

  beforeDestroy() {
    cancelAnimationFrame(this.rafID);
    window.removeEventListener('resize', this.resizeHandler);
  },

  methods: {
    resizeHandler() {
      this.$data.distance = this.$refs.scrollerContent.scrollWidth - this.$refs.scroller.offsetWidth;
      this.ratio = this.getIndexRatio(0);
      this.slideToRatio();
      setTimeout(() => {
        this.$data.state.touchDevice = isTouchDevice();
      }, 500);
    },

    movehandler(e) {
      if (this.$data.state.touchDevice) return;

      this.ratio = clamp(0, (e.clientX / (this.$refs.scroller.offsetWidth * 0.8)) - 0.1, 1);
      this.smoothMove = true;
      if (this.isMoving) return;
      this.$data.distance = this.$refs.scrollerContent.scrollWidth - this.$refs.scroller.offsetWidth;
      this.slideToRatio();
    },

    swipehandler(e) {
      if (!SWIPE_ENABLED) return;

      if (e.type === 'swipeend' || e.type === 'swipeabort') {
        this.$data.distance = this.$refs.scrollerContent.scrollWidth - this.$refs.scroller.offsetWidth;

        if (e.type === 'swipeend') {
          const itemCount = this.$props.items.length;
          const itemWidth = this.$refs.scrollerContent.scrollWidth / itemCount;
          const itemRatio = itemWidth / this.$data.distance;
          const offsetRatio = ((this.$refs.scroller.offsetWidth / this.$data.distance) - itemRatio) * 0.5;

          if (!this.isMouseOverNavigation()) {
            const indexStartItem = this.getCurrentItemIndex();
            const indexNextItem = clamp(0, indexStartItem + (this.swipeX > 0 ? -1 : 1), itemCount - 1);
            const nextRatio = (indexNextItem * itemRatio);
            this.ratio = nextRatio - offsetRatio;
          } else {
            this.ratio = clamp(0, this.getRatio(), 1);
          }
        }

        this.$data.translateX += this.$data.swipeX;
        this.$data.swipeX = 0;
        this.slideToRatio();

        return;
      }

      this.$data.swipeX = e.deltaX;
    },

    previous() {
      const targetIndex = clamp(0, this.getCurrentItemIndex() - 1, this.$props.items.length - 1);
      this.ratio = this.getIndexRatio(targetIndex);
      this.slideToRatio();
    },

    next() {
      const targetIndex = clamp(0, this.getCurrentItemIndex() + 1, this.$props.items.length - 1);
      this.ratio = this.getIndexRatio(targetIndex);
      this.slideToRatio();
    },

    slideToRatio() {
      this.$data.speed = this.smoothMove ? clamp(-SPEED_MAX, (this.ratio - this.getRatio()) * 0.09, SPEED_MAX) : 0;

      if (Math.abs(this.$data.speed) <= SPEED_MIN) {
        this.$data.translateX = -this.$data.distance * this.ratio;
        this.isMoving = false;
        this.rafID = null;
        return;
      }

      this.$data.index = this.getCurrentItemIndex();
      this.isMoving = true;
      this.$data.translateX -= this.$data.distance * this.$data.speed;
      this.rafID = requestAnimationFrame(this.slideToRatio);
    },

    getRatio() {
      return (-this.$data.translateX / this.$data.distance) || 0;
    },

    getIndexRatio(index) {
      const itemWidth = this.$refs.scrollerContent.scrollWidth / this.$props.items.length;
      const itemRatio = itemWidth / this.$data.distance;
      const offsetRatio = !this.isMouseOverNavigation()
        ? ((this.$refs.scroller.offsetWidth / this.$data.distance) - itemRatio) * 0.5
        : 0;
      const ratio = (itemRatio * index) - offsetRatio;
      return ratio;
    },

    getCurrentItemIndex() {
      this.$data.distance = this.$refs.scrollerContent.scrollWidth - this.$refs.scroller.offsetWidth;
      return Math.round((-this.$data.translateX / this.$data.distance) * (this.$props.items.length - 1));
    },

    isMouseOverNavigation() {
      const itemCount = this.$props.items.length;
      const itemWidth = this.$refs.scrollerContent.scrollWidth / itemCount;
      return this.$refs.scroller.offsetWidth > itemWidth * 2;
    },
  },
};
</script>

<style lang="stylus" scoped>
addHalfWidthDummy($color = blue) {
  position: relative;

  &:after {
    content: '';

    position: absolute;
    z-index: 2;
    top: 0;
    left: 50%;

    width: 1px;
    height: 100%;

    border-right: 1px dashed $color;
  }
}
.slider {
  position: relative;
  // addHalfWidthDummy();
}

.slider__swiper {
  position: relative;
  z-index: 0;
}

.slider__wrapper {
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  z-index: 0;
}

.slider__items {
  display: flex;
  flex-direction: row;
}

.slider__item {
  flex-shrink: 0;
  box-sizing: border-box;
  /*
  border-left: 1px dashed red;
  border-right: @border-left;
  addHalfWidthDummy(green);
  */
}

.slider__navigation {
  position: relative;
  z-index: 1;
}

.slider__button-wrapper {
  position: relative;
  z-index: 5;
  transition: opacity 0.25s ease-in-out;
}
</style>
